import type { History } from 'history';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { currentSubSession, onSubSessionChange } from './currentSubSession';
import { searchWithSubSessionId } from './subSessionUrl';

// The address bar wins on the same page: a raw `replaceState` leaves the router with a stale copy.
const addressBarSearchAndHash = (
  history: History,
): Pick<History['location'], 'search' | 'hash'> => {
  const { location } = history;
  const routerPathname = new URL(
    history.createHref({ pathname: location.pathname }),
    window.location.href,
  ).pathname;

  return routerPathname === window.location.pathname
    ? { search: window.location.search, hash: window.location.hash }
    : { search: location.search, hash: location.hash };
};

/**
 * Keeps `ibe.ss` in the address bar in sync with this tab's id through the router's `history`,
 * since a raw `replaceState` would be overwritten by the router's cached location.
 */
export const useSubSessionUrlSync = (): void => {
  const history = useHistory();

  useEffect(() => {
    const sync = (): void => {
      const subSessionId = currentSubSession();
      if (!subSessionId) {
        return;
      }

      const { location } = history;
      const addressBar = addressBarSearchAndHash(history);
      const search = searchWithSubSessionId(addressBar.search, subSessionId);
      if (search !== location.search || addressBar.hash !== location.hash) {
        history.replace({ ...location, search, hash: addressBar.hash });
      }
    };

    sync();
    const unsubscribe = onSubSessionChange(sync);
    // Restores the parameter after a navigation that dropped it.
    const unlisten = history.listen(sync);

    return () => {
      unsubscribe();
      unlisten();
    };
  }, [history]);
};
